// What the limit line and the range rings read of the hulls (R548).
//
// Cards 34 and 46 both draw round ONE thing: a settled Beacon, or the hull
// the commander selected. Neither is a field of `ChartScene`, which carries
// the water and the sites, so the two pieces share this reader instead of
// each keeping its own copy of the same three questions.
//
// **THE HULLS COME THROUGH `ChartScene.fleets`**, the one member the chart
// asks of a fleet layer. `MapScene` satisfies it with the layer it already
// holds, and a test hands over a stub of the same three calls, so there is
// no structural sniffing here and no second view of a hull. A scene without
// a fleet store — a page that draws no hulls — names no Beacon and no own
// hull; `settledBeacons` still answers off `scene.beams`, because the
// "always" reading of card 34 draws round every settled Beacon whether or
// not the pointer is on one.
//
// Nothing here keeps a reference past the call it was made in:
// `chartFleets()` hands back a buffer the fleet layer reuses (R131).

import { AnchorState, ShipClass } from '../../../gen/proto/aquaphobia';
import type { ChartContext, ChartFleet, ChartScene } from '../context';

/** One Beacon the two pieces draw round. */
export interface BeaconTarget {
  /** The fleet id, so a hover and a selection match the same hull. */
  id: number;
  x: number;
  y: number;
}

/** One own hull under the selection, for the plain rings of card 46. */
export interface HullTarget {
  id: number;
  x: number;
  y: number;
  /** The ship class (`ShipClass`), so a piece can keep a ruling to one class (R565). */
  cls: number;
}

/** A hull that lends its array to the squad, for the limit line of card 34. */
export interface OracleTarget {
  id: number;
  x: number;
  y: number;
  /** The listening reach this viewer gets through the hull, in world px. */
  sonarPx: number;
}

/** Every hull of the running scene, or nothing where there is no store. */
function hullsOf(scene: ChartScene): readonly ChartFleet[] {
  return scene.fleets?.chartFleets() ?? [];
}

/** The hull of an id, or null. */
function hullAt(scene: ChartScene, id: number): ChartFleet | null {
  return hullsOf(scene).find((f) => f.id === id) ?? null;
}

/** The fleet the pointer is over, or the one the commander selected. */
export function namedFleet(ctx: ChartContext): number {
  if (ctx.hover?.kind === 'fleet') return ctx.hover.id;
  if (ctx.selection?.kind === 'fleet') return ctx.selection.id;
  return 0;
}

/**
 * Is this fleet the one the pointer is over, or the one the commander
 * selected? Unlike `namedFleet`, both answer at once: a hover does not take
 * the name off the selection (R1445).
 */
export function isNamedFleet(ctx: ChartContext, id: number): boolean {
  return (ctx.hover?.kind === 'fleet' && ctx.hover.id === id)
    || (ctx.selection?.kind === 'fleet' && ctx.selection.id === id);
}

/** The site the pointer is over, or the one the commander selected, or 0. */
export function namedSite(ctx: ChartContext): number {
  if (ctx.hover?.kind === 'site') return ctx.hover.id;
  if (ctx.selection?.kind === 'site') return ctx.selection.id;
  return 0;
}

/**
 * Every settled Beacon on the map.
 *
 * `scene.beams` holds one entry per working sovereignty source, and a
 * Beacon radiates only while its anchor is SET (R114), so the beacon beams
 * ARE the settled Beacons. A source that beams twice is taken once.
 */
export function settledBeacons(scene: ChartScene): BeaconTarget[] {
  const seen = new Set<number>();
  const out: BeaconTarget[] = [];
  for (const b of scene.beams) {
    if (!b.beacon || seen.has(b.sourceId)) continue;
    seen.add(b.sourceId);
    out.push({ id: b.sourceId, x: b.x, y: b.y });
  }
  out.sort((a, b) => a.id - b.id);
  return out;
}

/**
 * The Beacon the pointer or the selection names, or null.
 *
 * The hull answers, and not the beam list, because the hull holds the
 * anchor state itself: a Beacon that settled stays named whether or not its
 * beam reached this frame. The class is the other half of the test. The
 * Sentinel took the same ANCHOR order in R661, and a planted tower radiates
 * nothing, so a settled anchor alone is no longer a Beacon.
 *
 * A foreign Beacon keeps its circle. `data/balance/ships.toml` marks the
 * class `support`, and the server sends `cls` for a support hull it shows
 * you (`hub/convert.go`), so this test needed no wire change.
 */
export function namedBeacon(ctx: ChartContext): BeaconTarget | null {
  const id = namedFleet(ctx);
  if (id === 0) return null;
  const f = hullAt(ctx.scene, id);
  return f && f.cls === ShipClass.SHIP_CLASS_BEACON
    && f.anchor === AnchorState.ANCHOR_STATE_SET
    ? { id, x: f.x, y: f.y }
    : null;
}

/** The own hull the pointer or the selection names, or null. */
export function namedOwnHull(ctx: ChartContext): HullTarget | null {
  const id = namedFleet(ctx);
  if (id === 0) return null;
  const f = hullAt(ctx.scene, id);
  return f && f.own ? { id, x: f.x, y: f.y, cls: f.cls } : null;
}

/**
 * Every hull that is lending its array to the squad now (R453).
 *
 * The limit line letters the reach a sharing Oracle gives, because that
 * circle is the one the whole squad hears through, and it is not the
 * Oracle's own ring on any other frame.
 */
export function sharingOracles(scene: ChartScene): OracleTarget[] {
  const out: OracleTarget[] = [];
  for (const f of hullsOf(scene)) {
    if (!f.sharing || f.sonarPx <= 0) continue;
    out.push({ id: f.id, x: f.x, y: f.y, sonarPx: f.sonarPx });
  }
  out.sort((a, b) => a.id - b.id);
  return out;
}

/** How many rings a settled Beacon takes, from the server's own catalog. */
export function anchorRingsOf(ctx: ChartContext): number {
  return ctx.state.catalog?.anchorRings || DEFAULT_ANCHOR_RINGS;
}

/**
 * What the client draws before a hello names the figure: a map drawn before
 * the catalog lands must still draw a ring.
 */
const DEFAULT_ANCHOR_RINGS = 5;
